import { Fragment } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { useBrand } from '../hooks/useBrand.jsx';
import { Spinner } from './ui.jsx';

/**
 * Gate for dashboard routes. `role` optionally narrows to creator, brand or admin.
 *
 * Note what this does NOT wrap: any public page. Programs, creator profiles and
 * both directories are readable by anyone, which is the product.
 */
export function RequireAuth({ role, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-ink-faint">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    // The query too: links in emails name the brand they are about (?brand=).
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // Admin is not a role anyone can add to themselves, so there is nowhere to
  // send them — just say so.
  if (role === 'admin' && !user.roles.includes('admin')) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <h1 className="text-xl font-semibold">Not available</h1>
        <p className="mt-2 text-sm text-ink-muted">This page is for Affily operators only.</p>
      </div>
    );
  }

  if (role && !user.roles.includes(role)) {
    return <Navigate to={`/dashboard/add-role?role=${role}`} replace />;
  }

  if (role === 'brand') return <BrandScoped>{children}</BrandScoped>;
  return children;
}

/**
 * Brand pages, remounted whenever the current brand changes. Every page loads
 * its own data on mount, so a remount is what makes Programs, Billing and the
 * rest show the newly selected brand — no page has to know about switching.
 * Waits for the brand list first, so a `?brand=` link is honoured before any
 * page asks the server for "my brand".
 */
function BrandScoped({ children }) {
  const { loaded, activeBrandId } = useBrand();
  if (!loaded) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-ink-faint">
        <Spinner />
      </div>
    );
  }
  return <Fragment key={activeBrandId ?? 'none'}>{children}</Fragment>;
}
